import { ColorScale } from '../state/sceneStore';

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export const getColorFromScale = (normalized: number, colorScale: ColorScale): RGB => {
  // For jet scale, don't clamp values above 1 - let them show the highest color
  const clamped = colorScale === 'jet' ? Math.max(0, normalized) : Math.max(0, Math.min(1, normalized));

  switch (colorScale) {
    case 'blue-red':
      return {
        r: Math.round(255 * clamped),
        g: 0,
        b: Math.round(255 * (1 - clamped))
      };

    case 'viridis':
      if (clamped < 0.25) {
        const t = clamped * 4;
        return { r: Math.round(68 * t), g: Math.round(1 * t), b: Math.round(84 * t) };
      } else if (clamped < 0.5) {
        const t = (clamped - 0.25) * 4;
        return { r: Math.round(68 + (59 - 68) * t), g: Math.round(1 + (82 - 1) * t), b: Math.round(84 + (139 - 84) * t) };
      } else if (clamped < 0.75) {
        const t = (clamped - 0.5) * 4;
        return { r: Math.round(59 + (33 - 59) * t), g: Math.round(82 + (144 - 82) * t), b: Math.round(139 + (140 - 139) * t) };
      } else {
        const t = (clamped - 0.75) * 4;
        return { r: Math.round(33 + (253 - 33) * t), g: Math.round(144 + (231 - 144) * t), b: Math.round(140 + (37 - 140) * t) };
      }

    case 'plasma':
      if (clamped < 0.25) {
        const t = clamped * 4;
        return { r: Math.round(13 * t), g: Math.round(8 * t), b: Math.round(135 * t) };
      } else if (clamped < 0.5) {
        const t = (clamped - 0.25) * 4;
        return { r: Math.round(13 + (75 - 13) * t), g: Math.round(8 + (3 - 8) * t), b: Math.round(135 + (160 - 135) * t) };
      } else if (clamped < 0.75) {
        const t = (clamped - 0.5) * 4;
        return { r: Math.round(75 + (190 - 75) * t), g: Math.round(3 + (40 - 3) * t), b: Math.round(160 + (100 - 160) * t) };
      } else {
        const t = (clamped - 0.75) * 4;
        return { r: Math.round(190 + (252 - 190) * t), g: Math.round(40 + (131 - 40) * t), b: Math.round(100 + (30 - 100) * t) };
      }

    case 'inferno':
      if (clamped < 0.25) {
        const t = clamped * 4;
        return { r: Math.round(0 * t), g: Math.round(0 * t), b: Math.round(4 * t) };
      } else if (clamped < 0.5) {
        const t = (clamped - 0.25) * 4;
        return { r: Math.round(0 + (87 - 0) * t), g: Math.round(0 + (15 - 0) * t), b: Math.round(4 + (125 - 4) * t) };
      } else if (clamped < 0.75) {
        const t = (clamped - 0.5) * 4;
        return { r: Math.round(87 + (220 - 87) * t), g: Math.round(15 + (55 - 15) * t), b: Math.round(125 + (20 - 125) * t) };
      } else {
        const t = (clamped - 0.75) * 4;
        return { r: Math.round(220 + (252 - 220) * t), g: Math.round(55 + (255 - 55) * t), b: Math.round(20 + (142 - 20) * t) };
      }

    case 'magma':
      if (clamped < 0.25) {
        const t = clamped * 4;
        return { r: Math.round(0 * t), g: Math.round(0 * t), b: Math.round(4 * t) };
      } else if (clamped < 0.5) {
        const t = (clamped - 0.25) * 4;
        return { r: Math.round(0 + (68 - 0) * t), g: Math.round(0 + (1 - 0) * t), b: Math.round(4 + (84 - 4) * t) };
      } else if (clamped < 0.75) {
        const t = (clamped - 0.5) * 4;
        return { r: Math.round(68 + (197 - 68) * t), g: Math.round(1 + (1 - 1) * t), b: Math.round(84 + (133 - 84) * t) };
      } else {
        const t = (clamped - 0.75) * 4;
        return { r: Math.round(197 + (252 - 197) * t), g: Math.round(1 + (255 - 1) * t), b: Math.round(133 + (131 - 133) * t) };
      }

    case 'turbo':
      if (clamped < 0.25) {
        const t = clamped * 4;
        return { r: Math.round(35 * t), g: Math.round(23 * t), b: Math.round(27 * t) };
      } else if (clamped < 0.5) {
        const t = (clamped - 0.25) * 4;
        return { r: Math.round(35 + (25 - 35) * t), g: Math.round(23 + (146 - 23) * t), b: Math.round(27 + (222 - 27) * t) };
      } else if (clamped < 0.75) {
        const t = (clamped - 0.5) * 4;
        return { r: Math.round(25 + (25 - 25) * t), g: Math.round(146 + (146 - 146) * t), b: Math.round(222 + (222 - 222) * t) };
      } else {
        const t = (clamped - 0.75) * 4;
        return { r: Math.round(25 + (230 - 25) * t), g: Math.round(146 + (146 - 146) * t), b: Math.round(222 + (25 - 222) * t) };
      }

    case 'jet':
      if (clamped < 0.125) {
        const t = clamped * 8;
        return { r: 0, g: 0, b: Math.round(127 + 128 * t) };
      } else if (clamped < 0.375) {
        const t = (clamped - 0.125) * 4;
        return { r: 0, g: Math.round(255 * t), b: 255 };
      } else if (clamped < 0.625) {
        const t = (clamped - 0.375) * 4;
        return { r: Math.round(255 * t), g: 255, b: Math.round(255 * (1 - t)) };
      } else if (clamped < 0.875) {
        const t = (clamped - 0.625) * 4;
        return { r: 255, g: Math.round(255 * (1 - t)), b: 0 };
      } else {
        // Values >= 0.875 - show highest color (red)
        return { r: 255, g: 0, b: 0 };
      }

    default:
      return { r: 0, g: 0, b: 0 };
  }
};

export const COLOR_SCALE_LABELS: Record<ColorScale, string> = {
  'blue-red': 'Blue-Red',
  'jet': 'Jet',
  'viridis': 'Viridis',
  'plasma': 'Plasma',
  'inferno': 'Inferno',
  'magma': 'Magma',
  'turbo': 'Turbo',
};
